import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Conversation, Message } from '../../../services/chatService';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { getMediaUrl } from '../../../utils/media';
import { ChatInput } from './ChatInput';
import { User } from '../../../types';

interface ChatWindowProps {
    conversation: Conversation | null;
    messages: Message[];
    currentUser: User | null;
    onSendMessage: (content: string, files?: any) => Promise<void | any>;
    loadingMessages: boolean;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
    conversation,
    messages,
    currentUser,
    onSendMessage,
    loadingMessages
}) => {
    const { t, i18n } = useTranslation('common');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loadingMessages]);

    if (!conversation) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-400">
                <div className="text-6xl mb-4">💬</div>
                <p>{t('chat.selectConversation', { defaultValue: 'Select a conversation to start chatting' })}</p>
            </div>
        );
    }

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="flex-1 flex flex-col h-full bg-white">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
                <div className="flex items-center gap-3">
                    <Avatar>
                        <AvatarImage src={getMediaUrl(conversation.other_user.avatar)} />
                        <AvatarFallback>{(conversation.other_user.full_name || conversation.other_user.username)[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                        <h3 className="font-bold">{conversation.other_user.full_name || conversation.other_user.username}</h3>
                        <span className={`text-xs flex items-center gap-1 ${conversation.other_user.is_online ? 'text-green-600' : 'text-gray-500'}`}>
                            {conversation.other_user.is_online ? (
                                <><span className="inline">●</span> {t('chat.status.online', { defaultValue: 'Online' })}</>
                            ) : (
                                <>{t('chat.status.offline', { defaultValue: 'Offline' })}</>
                            )}
                        </span>
                    </div>
                </div>
                {/* Actions */}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                {loadingMessages && <div className="text-center text-xs text-gray-500">{t('chat.loading', { defaultValue: 'Loading...' })}</div>}

                {messages.map((msg, index) => {
                    const isMe = msg.sender_id === currentUser?.id;
                    return (
                        <div key={msg.id || index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            {!isMe && (
                                <Avatar className="w-8 h-8 mr-2 mt-1">
                                    <AvatarImage src={getMediaUrl(conversation.other_user.avatar)} />
                                    <AvatarFallback>{conversation.other_user.username[0]}</AvatarFallback>
                                </Avatar>
                            )}
                            <div className={`max-w-[70%] rounded-2xl px-4 py-2 shadow-sm ${isMe
                                ? 'bg-blue-600 text-white rounded-tr-none'
                                : 'bg-white text-gray-900 border border-gray-100 rounded-tl-none'
                                }`}>
                                {msg.content && <div className="whitespace-pre-wrap break-words">{msg.content}</div>}
                                {/* Attachments rendering */}
                                {msg.attachment_url && (
                                    <div className="mt-2 rounded-lg overflow-hidden bg-black/10">
                                        {msg.attachment_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                            <img src={getMediaUrl(msg.attachment_url)} alt="Attachment" className="max-w-full h-auto" />
                                        ) : (
                                            <a href={getMediaUrl(msg.attachment_url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 text-xs underline">
                                                📎 Attachment
                                            </a>
                                        )}
                                    </div>
                                )}
                                <div className={`text-[10px] mt-1 flex justify-end opacity-70 ${isMe ? 'text-blue-100' : 'text-gray-400'}`}>
                                    {formatTime(msg.created_at)}
                                    {isMe && msg.is_read && <span className="ml-1">✓✓</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput onSendMessage={onSendMessage} />
        </div>
    );
};
